import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Check, X, Lightning, Crown, Rocket, Warning, SignOut } from "@phosphor-icons/react";
import { useTranslation } from '../context/LanguageContext';
import { PLAN_CONFIGS, redirectToStripe, BillingCycle, requestCancelSubscription } from '../services/subscriptionService';
import { useAuth } from '../context/AuthContext';
import AuthModal from '../components/AuthModal';
import SEO from '../components/SEO';

const PageContainer = styled.div`
  background: #0d1117;
  color: white;
  min-height: 100vh;
  padding: 8rem 2rem 4rem;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  h1 {
    font-size: 4rem;
    font-weight: 800;
    line-height: 1.1;
    margin-bottom: 1.5rem;
    background: linear-gradient(90deg, #fff, #b3b3b3);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  p {
    font-size: 1.25rem;
    color: #8b949e;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const BillingToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 4rem;

  span {
    font-size: 1rem;
    color: #8b949e;
    font-weight: 500;
  }

  .active {
    color: #fff;
  }
`;

const ToggleSwitch = styled.button<{ $isYearly: boolean }>`
  position: relative;
  width: 56px;
  height: 30px;
  background: ${props => props.$isYearly ? '#3AB8FF' : '#30363d'};
  border-radius: 30px;
  border: none;
  cursor: pointer;
  transition: all 0.3s;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    inset-inline-start: ${props => props.$isYearly ? '29px' : '3px'};
    width: 24px;
    height: 24px;
    background: white;
    border-radius: 50%;
    transition: all 0.3s;
  }
`;

const SaveBadge = styled.span`
  background: linear-gradient(135deg, #3AB8FF, #1e90ff);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const PricingCard = styled(motion.div) <{ $featured?: boolean }>`
  background: ${props => props.$featured
    ? 'linear-gradient(135deg, rgba(58, 184, 255, 0.1), rgba(30, 144, 255, 0.05))'
    : 'rgba(255, 255, 255, 0.03)'};
  border: 2px solid ${props => props.$featured ? '#3AB8FF' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 24px;
  padding: 2.5rem;
  position: relative;
  transition: all 0.3s;
  transform-style: preserve-3d;

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.$featured
    ? '0 20px 60px rgba(58, 184, 255, 0.3)'
    : '0 20px 60px rgba(0, 0, 0, 0.3)'};
  }
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: -12px;
  inset-inline-start: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #3AB8FF, #1e90ff);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(58, 184, 255, 0.4);
`;

const PlanIcon = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  background: ${props => props.$color};
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  svg {
    font-size: 28px;
    color: white;
  }
`;

const PlanName = styled.h3`
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const PlanDescription = styled.p`
  color: #8b949e;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
  min-height: 40px;
`;

const PriceContainer = styled.div`
  margin-bottom: 2rem;
`;

const Price = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.5rem;

  .currency {
    font-size: 1.5rem;
    font-weight: 600;
  }

  .amount {
    font-size: 3.5rem;
    font-weight: 800;
    line-height: 1;
  }

  .period {
    font-size: 1rem;
    color: #8b949e;
  }
`;

const OriginalPrice = styled.span`
  color: #8b949e;
  text-decoration: line-through;
  font-size: 1rem;
`;

const CTAButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  width: 100%;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$variant === 'primary'
    ? 'linear-gradient(135deg, #3AB8FF, #1e90ff)'
    : 'rgba(255, 255, 255, 0.05)'};
  color: white;
  border: ${props => props.$variant === 'secondary' ? '2px solid rgba(255, 255, 255, 0.1)' : 'none'};

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.$variant === 'primary'
    ? 'linear-gradient(135deg, #50c1ff, #3a9eff)'
    : 'rgba(255, 255, 255, 0.08)'};
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0 0 0;
`;

const FeatureItem = styled.li<{ $included: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  color: ${props => props.$included ? '#fff' : '#8b949e'};
  font-size: 0.95rem;

  svg {
    font-size: 20px;
    flex-shrink: 0;
    color: ${props => props.$included ? '#3AB8FF' : '#8b949e'};
  }
`;

const SegmentSelector = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem 0 3rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 4px;
  max-width: 600px;
  margin: 2rem auto 3rem;
`;

const SegmentButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  background: ${props => props.$active ? 'rgba(58, 184, 255, 0.2)' : 'transparent'};
  color: ${props => props.$active ? '#3AB8FF' : '#8b949e'};
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  font-size: 1rem;
  
  &:hover {
    background: ${props => props.$active ? 'rgba(58, 184, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  }
`;

const PricingSection = styled.div`
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, #fff, #b3b3b3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.25rem;
  color: #8b949e;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0 1rem;
  }
`;

const PlanTagline = styled.span`
  display: inline-block;
  background: rgba(58, 184, 255, 0.1);
  color: #3AB8FF;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const ComparisonSection = styled.div`
  max-width: 1200px;
  margin: 6rem auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
    margin: 4rem auto;
  }
`;

const Table = styled.div`
  background: rgba(22, 27, 34, 0.6);
  border: 1px solid #30363d;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 4rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const TableRow = styled.div`
  display: flex;
  border-bottom: 1px solid #30363d;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TableHeader = styled.div`
  flex: 1;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(90deg, rgba(58, 184, 255, 0.1), rgba(58, 184, 255, 0.05));
  font-weight: 600;
  color: #f0f6fc;
  min-width: 200px;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-inline-end: 1px solid rgba(255, 255, 255, 0.05);
  
  &:first-child {
    background: transparent;
    font-weight: 700;
    color: #3AB8FF;
  }
  
  @media (max-width: 768px) {
    border-bottom: 1px solid #30363d;
  }
`;

const TableCell = styled.div<{ center?: boolean }>`
  flex: 1;
  padding: 1.25rem 1.5rem;
  color: #c9d1d9;
  display: flex;
  align-items: center;
  justify-content: ${props => props.center ? 'center' : 'flex-start'};
  border-inline-end: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background: rgba(13, 17, 23, 0.3);
  
  &:first-child {
    background: transparent;
    font-weight: 500;
    color: #f0f6fc;
  }
  
  &:last-child {
    border-inline-end: none;
  }
  
  &:not(:first-child) {
    text-align: center;
    justify-content: center;
  }
  
  @media (max-width: 768px) {
    justify-content: space-between;
    border-inline-end: none;
    border-bottom: 1px solid #30363d;
    
    &::before {
      content: attr(data-label);
      color: #c9d1d9;
      font-weight: 600;
      margin-inline-end: 1rem;
    }
  }
`;

const FeatureName = styled.span`
  color: #f0f6fc;
  font-weight: 500;
`;

const CheckIcon = styled(Check)`
  color: #3AB8FF;
  width: 20px;
  height: 20px;
  background: rgba(58, 184, 255, 0.1);
  padding: 4px;
  border-radius: 50%;
`;

const XIcon = styled(X)`
  color: #f85149;
  width: 20px;
  height: 20px;
  opacity: 0.6;
`;

const FAQSection = styled.div`
  max-width: 800px;
  margin: 8rem auto;
  padding: 0 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    margin: 6rem auto;
    padding: 0 1.5rem;
  }
`;

const FAQTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(90deg, #3AB8FF, #FFFFFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const FAQContent = styled.div`
  background: rgba(13, 17, 23, 0.9);
  border-radius: 12px;
  padding: 2.5rem 2rem;
  border: 1px solid #30363d;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const FAQText = styled.p`
  font-size: 1.1rem;
  color: #8b949e;
  margin: 1.5rem auto 2.5rem;
  line-height: 1.7;
  max-width: 700px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin: 1.25rem auto 2rem;
  }
`;

const FAQButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: #3AB8FF;
  padding: 0.5rem 1rem;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  
  &:hover {
    color: #6ec6ff;
    
    svg {
      transform: translateX(2px);
    }
  }
  
  svg {
    transition: transform 0.2s ease;
    width: 1em;
    height: 1em;
  }
`;

// Cancel Subscription Section Styles
const CancelSection = styled.div`
  max-width: 700px;
  margin: 2rem auto 4rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 2.5rem;
  text-align: center;
`;

const CancelTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: #f0f6fc;
`;

const CancelDescription = styled.p`
  color: #8b949e;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const CancelButton = styled.button`
  padding: 0.85rem 2rem;
  background: rgba(248, 81, 73, 0.1);
  color: #f85149;
  border: 1px solid rgba(248, 81, 73, 0.3);
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(248, 81, 73, 0.2);
    border-color: rgba(248, 81, 73, 0.5);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SignInPromptButton = styled.button`
  padding: 0.85rem 2rem;
  background: rgba(58, 184, 255, 0.1);
  color: #3AB8FF;
  border: 1px solid rgba(58, 184, 255, 0.3);
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(58, 184, 255, 0.2);
    border-color: rgba(58, 184, 255, 0.5);
  }
`;

const SuccessMessage = styled.div`
  background: rgba(46, 160, 67, 0.1);
  border: 1px solid rgba(46, 160, 67, 0.3);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  color: #3fb950;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const ConfirmOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ConfirmModal = styled(motion.div)`
  background: #1c1c1c;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2.5rem;
  max-width: 420px;
  width: 100%;
  text-align: center;
`;

const ConfirmTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: #f85149;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const ConfirmText = styled.p`
  color: #8b949e;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const ConfirmKeep = styled.button`
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ConfirmCancel = styled.button`
  padding: 0.75rem 1.5rem;
  background: rgba(248, 81, 73, 0.15);
  color: #f85149;
  border: 1px solid rgba(248, 81, 73, 0.3);
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(248, 81, 73, 0.25);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

interface Feature {
  name: string;
  included: boolean;
}

interface Plan {
  name: string;
  icon: React.ReactNode;
  iconColor: string;
  description: string;
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  segment: 'home' | 'teacher' | 'enterprise';
  features: Feature[];
  featured?: boolean;
  tagline?: string;
}

const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [isYearly, setIsYearly] = useState(true);
  const [activeSegment, setActiveSegment] = useState<'home' | 'teacher' | 'enterprise'>('teacher');

  // Auth Modal State
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [selectedPlanForAuth, setSelectedPlanForAuth] = useState<{ id: 'home' | 'plus' | 'pro', name: string } | null>(null);

  // Cancel Subscription State
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [authForCancel, setAuthForCancel] = useState(false);

  const getFeatureValue = (planName: string, featureName: string, plan: Plan) => {
    if (featureName === 'AI Assistant Credits') {
      if (planName === 'Starter') return '20/month';
      if (planName === 'Plus') return '300/month';
      if (planName === 'Pro') return 'Unlimited';
      return 'Unlimited';
    }

    if (featureName === 'GradeWizard Pages') {
      if (planName === 'Starter') return '30/month';
      if (planName === 'Plus') return '500/month';
      if (planName === 'Pro') return '1,500/month';
      return 'Custom';
    }

    if (featureName === 'Library Storage') {
      if (planName === 'Starter') return '5GB';
      if (planName === 'Plus') return '10GB';
      if (planName === 'Pro') return '30GB';
      return 'Custom';
    }

    if (featureName === 'Interactive Digital Exams') {
      if (planName === 'Starter') return '1/month';
      if (planName === 'Plus') return '25/month';
      if (planName === 'Pro') return 'Unlimited';
      return 'Unlimited';
    }

    if (featureName === 'Printable PDF Exams') {
      if (planName === 'Starter') return <CheckIcon weight="bold" />;
      return <CheckIcon weight="bold" />;
    }

    if (featureName === 'Interactive Learning Games') {
      if (planName === 'Pro') return '10';
      if (planName === 'Enterprise') return '10+';
      return <XIcon weight="bold" />;
    }

    if (featureName === 'Support Level') {
      if (planName === 'Starter') return 'Community';
      if (planName === 'Plus') return 'Priority';
      if (planName === 'Pro') return 'Live Chat';
      return '24/7 Priority';
    }

    if (featureName === 'Analytics Dashboard') {
      if (planName === 'Starter') return <XIcon weight="bold" />;
      return <CheckIcon weight="bold" />;
    }

    return '-';
  };

  const homePlans: Plan[] = [
    {
      name: 'SparkSkool Home',
      icon: <Lightning weight="fill" />,
      iconColor: 'linear-gradient(135deg, #FF6B6B, #EE5A6F)',
      description: PLAN_CONFIGS.home.tagline,
      monthlyPrice: PLAN_CONFIGS.home.price.monthly,
      yearlyPrice: PLAN_CONFIGS.home.price.yearly / 12,
      segment: 'home' as const,
      features: [
        { name: 'Up to 3 child profiles', included: true },
        { name: '100 AI Assistant Credits/month', included: true },
        { name: '3 Interactive Learning Games', included: true },
        { name: '15 Practice quizzes/month', included: true },
        { name: 'GradeWizard: 20 pages/month', included: true },
        { name: '5GB Library storage', included: true },
        { name: 'Community & Knowledge Base support', included: true },
      ]
    }
  ];

  const teacherPlans: Plan[] = [
    {
      name: 'Starter',
      icon: <Lightning weight="fill" />,
      iconColor: 'linear-gradient(135deg, #4CAF50, #66BB6A)',
      description: PLAN_CONFIGS.starter.tagline,
      monthlyPrice: PLAN_CONFIGS.starter.price.monthly,
      yearlyPrice: PLAN_CONFIGS.starter.price.yearly,
      segment: 'teacher' as const,
      tagline: 'Free Forever',
      features: [
        { name: '20 AI Assistant Credits/month', included: true },
        { name: 'GradeWizard: 30 pages/month', included: true },
        { name: 'Unlimited printable PDF exams', included: true },
        { name: '1 Interactive digital exam/month', included: true },
        { name: '5GB Library storage', included: true },
        { name: 'Community support', included: true },
        { name: 'Advanced analytics', included: false },
      ]
    },
    {
      name: 'Plus',
      icon: <Crown weight="fill" />,
      iconColor: 'linear-gradient(135deg, #3AB8FF, #1e90ff)',
      description: PLAN_CONFIGS.plus.tagline,
      monthlyPrice: PLAN_CONFIGS.plus.price.monthly,
      yearlyPrice: PLAN_CONFIGS.plus.price.yearly / 12,
      featured: true,
      segment: 'teacher',
      features: [
        { name: 'Everything in Starter, plus:', included: true },
        { name: '300 AI Assistant Credits/month', included: true },
        { name: 'GradeWizard: 500 pages/month', included: true },
        { name: 'Unlimited printable PDF exams', included: true },
        { name: '25 Interactive digital exams/month', included: true },
        { name: '10GB Library storage', included: true },
        { name: 'Advanced analytics dashboard', included: true },
        { name: 'Priority support', included: true },
      ]
    },
    {
      name: 'Pro',
      icon: <Rocket weight="fill" />,
      iconColor: 'linear-gradient(135deg, #9D50BB, #6E48AA)',
      description: PLAN_CONFIGS.pro.tagline,
      monthlyPrice: PLAN_CONFIGS.pro.price.monthly, // 25
      yearlyPrice: PLAN_CONFIGS.pro.price.yearly / 12, // ~20.83 → displays as $21
      segment: 'teacher',
      features: [
        { name: 'Everything in Plus, plus:', included: true },
        { name: 'Unlimited AI Assistant Credits', included: true },
        { name: 'GradeWizard: 1,500 pages/month', included: true },
        { name: '30GB Library storage', included: true },
        { name: '10 Interactive Learning Games', included: true },
        { name: 'Unlimited printable & digital exams', included: true },
        { name: 'Early access to Experience Lab', included: true },
        { name: 'Live chat support', included: true },
      ]
    }
  ];

  const enterprisePlan: Plan = {
    name: 'Enterprise',
    icon: <Rocket weight="fill" />,
    iconColor: 'linear-gradient(135deg, #FF9800, #F57C00)',
    description: 'For schools, districts, and institutions',
    monthlyPrice: null,
    yearlyPrice: null,
    segment: 'enterprise' as const,
    features: [
      { name: 'Everything in Pro for all staff', included: true },
      { name: 'Unlimited users', included: true },
      { name: 'Dedicated account manager', included: true },
      { name: 'Custom integrations & SSO', included: true },
      { name: 'Advanced security & compliance', included: true },
      { name: 'Custom training & onboarding', included: true },
      { name: 'Priority technical support', included: true },
    ]
  };

  const faqs = [
    {
      question: 'Have more questions?',
      answer: 'Get answers to commonly asked questions about pricing, subscription tiers, features, best practices, how our AI works, usage, and more!'
    }
  ];

  return (
    <PageContainer>
      <SEO titleKey="Pricing" />
      <ContentWrapper>
        <HeroSection>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Simple, Transparent Pricing
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            Choose the perfect plan for your teaching journey.
          </motion.p>
        </HeroSection>

        <BillingToggle>
          <span className={!isYearly ? 'active' : ''}>Monthly</span>
          <ToggleSwitch
            $isYearly={isYearly}
            onClick={() => setIsYearly(!isYearly)}
            aria-label="Toggle billing period"
          />
          <span className={isYearly ? 'active' : ''}>Yearly</span>
          {isYearly && <SaveBadge>Save 20%</SaveBadge>}
        </BillingToggle>

        <SegmentSelector>
          <SegmentButton
            $active={activeSegment === 'home'}
            onClick={() => setActiveSegment('home')}
          >
            For Home
          </SegmentButton>
          <SegmentButton
            $active={activeSegment === 'teacher'}
            onClick={() => setActiveSegment('teacher')}
          >
            For Teachers
          </SegmentButton>
          <SegmentButton
            $active={activeSegment === 'enterprise'}
            onClick={() => setActiveSegment('enterprise')}
          >
            For Schools
          </SegmentButton>
        </SegmentSelector>

        {activeSegment === 'home' && (
          <PricingSection>
            <SectionTitle>For Home</SectionTitle>
            <SectionDescription>Perfect for parents, tutors, and homeschoolers who want to create engaging, AI-powered learning experiences.</SectionDescription>
            <PricingGrid>
              {homePlans.map((plan: Plan, index: number) => (
                <PricingCard
                  key={plan.name}
                  $featured={plan.featured}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  {plan.featured && (
                    <FeaturedBadge>
                      <Crown weight="fill" size={16} />
                      Most Popular
                    </FeaturedBadge>
                  )}

                  <PlanIcon $color={plan.iconColor}>
                    {plan.icon}
                  </PlanIcon>

                  <PlanName>{plan.name}</PlanName>
                  <PlanDescription>{plan.description}</PlanDescription>

                  <PriceContainer>
                    {plan.monthlyPrice ? (
                      <>
                        <Price>
                          <span className="currency">$</span>
                          <span className="amount">
                            {isYearly
                              ? Math.round((plan.yearlyPrice || 0))
                              : plan.monthlyPrice}
                          </span>
                          <span className="period">/mo</span>
                        </Price>
                        {isYearly && plan.yearlyPrice !== null && (
                          <div style={{ fontSize: '0.9rem', color: '#8b949e', marginTop: '0.25rem' }}>
                            Billed ${Math.round((plan.yearlyPrice || 0) * 12)}/year
                          </div>
                        )}
                        {isYearly && (
                          <OriginalPrice>
                            ${plan.monthlyPrice}/mo if billed monthly
                          </OriginalPrice>
                        )}
                      </>
                    ) : (
                      <Price>
                        <span className="amount" style={{ fontSize: '2rem' }}>
                          Custom
                        </span>
                      </Price>
                    )}
                  </PriceContainer>

                  <CTAButton
                    $variant="primary"
                    onClick={() => {
                      if (user) {
                        redirectToStripe('home', (isYearly ? 'yearly' : 'monthly') as BillingCycle, user.uid, user.email || undefined);
                      } else {
                        setSelectedPlanForAuth({ id: 'home', name: 'SparkSkool Home' });
                        setAuthForCancel(false);
                        setIsAuthModalOpen(true);
                      }
                    }}
                  >
                    Get Started
                  </CTAButton>

                  <FeaturesList>
                    {plan.features.map((feature) => (
                      <FeatureItem key={feature.name} $included={feature.included}>
                        {feature.included ? (
                          <Check weight="bold" />
                        ) : (
                          <X weight="bold" />
                        )}
                        {feature.name}
                      </FeatureItem>
                    ))}
                  </FeaturesList>
                </PricingCard>
              ))}
            </PricingGrid>
          </PricingSection>
        )}

        {activeSegment === 'teacher' && (
          <PricingSection>
            <SectionTitle>For Teachers</SectionTitle>
            <SectionDescription>AI tools to save time, reduce burnout, and enhance teaching.</SectionDescription>
            <PricingGrid>
              {teacherPlans.map((plan: Plan, index: number) => (
                <PricingCard
                  key={plan.name}
                  $featured={plan.featured}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  {plan.featured && (
                    <FeaturedBadge>
                      <Crown weight="fill" size={16} />
                      Most Popular
                    </FeaturedBadge>
                  )}
                  <PlanIcon $color={plan.iconColor}>
                    {plan.icon}
                  </PlanIcon>
                  <PlanName>{plan.name}</PlanName>
                  {plan.tagline && <PlanTagline>{plan.tagline}</PlanTagline>}
                  <PlanDescription>{plan.description}</PlanDescription>
                  <PriceContainer>
                    {plan.monthlyPrice !== null ? (
                      <>
                        <Price>
                          {plan.monthlyPrice === 0 ? (
                            <span className="amount" style={{ fontSize: '3rem' }}>Free</span>
                          ) : (
                            <>
                              <span className="currency">$</span>
                              <span className="amount">
                                {isYearly
                                  ? Math.round(plan.yearlyPrice || 0)
                                  : plan.monthlyPrice}
                              </span>
                              <span className="period">/mo</span>
                            </>
                          )}
                        </Price>
                        {plan.monthlyPrice > 0 && isYearly && plan.yearlyPrice !== null && (
                          <div style={{ fontSize: '0.9rem', color: '#8b949e', marginTop: '0.25rem' }}>
                            Billed ${Math.round((plan.yearlyPrice || 0) * 12)}/year
                          </div>
                        )}
                        {plan.monthlyPrice > 0 && isYearly && plan.yearlyPrice !== null && (
                          <OriginalPrice>
                            ${plan.monthlyPrice}/mo if billed monthly
                          </OriginalPrice>
                        )}
                      </>
                    ) : (
                      <Price>
                        <span className="amount" style={{ fontSize: '2rem' }}>
                          Custom
                        </span>
                      </Price>
                    )}
                  </PriceContainer>
                  <CTAButton
                    $variant={plan.featured ? 'primary' : 'secondary'}
                    onClick={() => {
                      if (plan.name === 'Plus' || plan.name === 'Pro') {
                        const planId = plan.name.toLowerCase() as 'home' | 'plus' | 'pro';
                        if (user) {
                          redirectToStripe(planId, (isYearly ? 'yearly' : 'monthly') as BillingCycle, user.uid, user.email || undefined);
                        } else {
                          setSelectedPlanForAuth({ id: planId, name: plan.name });
                          setAuthForCancel(false);
                          setIsAuthModalOpen(true);
                        }
                      } else if (plan.monthlyPrice === 0) {
                        // Starter free plan — redirect to app
                        window.open('https://app.sparkskool.com', '_blank');
                      }
                    }}
                  >
                    {plan.monthlyPrice === 0 ? 'Get Started' : 'Get Started'}
                  </CTAButton>
                  <FeaturesList>
                    {plan.features.map((feature: Feature) => (
                      <FeatureItem key={feature.name} $included={feature.included}>
                        {feature.included ? (
                          <Check weight="bold" />
                        ) : (
                          <X weight="bold" />
                        )}
                        {feature.name}
                      </FeatureItem>
                    ))}
                  </FeaturesList>
                </PricingCard>
              ))}
            </PricingGrid>
          </PricingSection>
        )}

        {activeSegment === 'enterprise' && (
          <PricingSection>
            <SectionTitle>For Schools & Districts</SectionTitle>
            <SectionDescription>Enterprise solution for institutions ready to deploy AI-powered learning at scale.</SectionDescription>
            <PricingGrid>
              <PricingCard
                $featured={false}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <PlanIcon $color={enterprisePlan.iconColor}>
                  {enterprisePlan.icon}
                </PlanIcon>
                <PlanName>{enterprisePlan.name}</PlanName>
                <PlanDescription>{enterprisePlan.description}</PlanDescription>
                <PriceContainer>
                  <Price>
                    <span className="amount" style={{ fontSize: '2rem' }}>
                      Custom Pricing
                    </span>
                  </Price>
                </PriceContainer>
                <CTAButton
                  $variant="primary"
                  data-cal-link="sparkskool/20-min"
                  data-cal-namespace="20-min"
                  data-cal-config='{"layout":"month_view"}'
                >
                  Contact Sales
                </CTAButton>
                <FeaturesList>
                  {enterprisePlan.features.map((feature: Feature) => (
                    <FeatureItem key={feature.name} $included={true}>
                      <Check weight="bold" />
                      {feature.name}
                    </FeatureItem>
                  ))}
                </FeaturesList>
              </PricingCard>
            </PricingGrid>
          </PricingSection>
        )}

        <ComparisonSection>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div style={{
              display: 'inline-block',
              background: 'rgba(58, 184, 255, 0.1)',
              color: '#3AB8FF',
              padding: '0.5rem 1.25rem',
              borderRadius: '20px',
              marginBottom: '1rem',
              fontSize: '0.9rem',
              fontWeight: 600,
              letterSpacing: '0.5px'
            }}>
              Plan Comparison
            </div>
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: 700,
              textAlign: 'center',
              margin: '0.5rem 0',
              background: 'linear-gradient(90deg, #3AB8FF, #FFFFFF)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Choose the Right Plan
            </h2>
            <p style={{
              color: '#8b949e',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6',
              fontSize: '1.1rem'
            }}>
              Compare features and find the perfect plan for your needs
            </p>
          </div>

          <Table>
            {/* Header */}
            <TableRow>
              <TableHeader>Features</TableHeader>
              <TableHeader>Starter</TableHeader>
              <TableHeader>Plus</TableHeader>
              <TableHeader>Pro</TableHeader>
              <TableHeader>Enterprise</TableHeader>
            </TableRow>

            {/* Rows */}
            {[
              'AI Assistant Credits',
              'GradeWizard Pages',
              'Library Storage',
              'Interactive Digital Exams',
              'Printable PDF Exams',
              'Interactive Learning Games',
              'Support Level',
              'Analytics Dashboard'
            ].map((feature, rowIndex) => (
              <TableRow key={rowIndex}>
                <TableCell data-label="Feature">
                  <FeatureName>{feature}</FeatureName>
                </TableCell>
                <TableCell data-label="Starter">
                  {getFeatureValue('Starter', feature, teacherPlans[0])}
                </TableCell>
                <TableCell data-label="Plus">
                  {getFeatureValue('Plus', feature, teacherPlans[1])}
                </TableCell>
                <TableCell data-label="Pro">
                  {getFeatureValue('Pro', feature, teacherPlans[2])}
                </TableCell>
                <TableCell data-label="Enterprise">
                  {getFeatureValue('Enterprise', feature, enterprisePlan)}
                </TableCell>
              </TableRow>
            ))}
          </Table>
        </ComparisonSection>

        <FAQSection id="faq">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <FAQContent>
              <FAQTitle>Have more questions?</FAQTitle>
              <FAQText>
                Get answers to commonly asked questions about pricing, subscription tiers, features,
                best practices, how our AI works, usage, and more!
              </FAQText>
              <FAQButton href="#faq">
                View All FAQs
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" viewBox="0 0 256 256">
                  <path d="M224.49,136.49l-72,72a12,12,0,0,1-17-17L187,140H40a12,12,0,0,1,0-24H187L135.51,64.48a12,12,0,0,1,17-17l72,72A12,12,0,0,1,224.49,136.49Z"></path>
                </svg>
              </FAQButton>
            </FAQContent>
          </motion.div>
        </FAQSection>

        {/* Cancel Subscription Section */}
        <CancelSection>
          <CancelTitle>Manage Your Subscription</CancelTitle>
          {!user ? (
            <>
              <CancelDescription>
                Sign in to your account to manage or cancel your subscription.
              </CancelDescription>
              <SignInPromptButton onClick={() => {
                setAuthForCancel(true);
                setSelectedPlanForAuth(null);
                setIsAuthModalOpen(true);
              }}>
                Sign In to Manage
              </SignInPromptButton>
            </>
          ) : cancelSuccess ? (
            <SuccessMessage>
              <Check weight="bold" size={20} />
              Your cancellation request has been submitted. Your subscription will remain active until the end of the current billing period. We'll send a confirmation to {user.email}.
            </SuccessMessage>
          ) : (
            <>
              <CancelDescription>
                Signed in as <strong style={{ color: '#f0f6fc' }}>{user.email}</strong>. Need to cancel your subscription? Your access will continue until the end of your current billing period.
              </CancelDescription>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <CancelButton onClick={() => setShowCancelConfirm(true)}>
                  <SignOut size={18} />
                  Cancel Subscription
                </CancelButton>
                <SignInPromptButton onClick={logout} style={{ background: 'rgba(255,255,255,0.04)', color: '#8b949e', borderColor: 'rgba(255,255,255,0.1)' }}>
                  Sign Out
                </SignInPromptButton>
              </div>
            </>
          )}
        </CancelSection>

      </ContentWrapper>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <ConfirmOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowCancelConfirm(false)}
        >
          <ConfirmModal
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <ConfirmTitle>
              <Warning size={22} weight="fill" />
              Cancel Subscription?
            </ConfirmTitle>
            <ConfirmText>
              Are you sure you want to cancel your subscription? Your access will continue until the end of your current billing period. You can always re-subscribe later.
            </ConfirmText>
            <ConfirmActions>
              <ConfirmKeep onClick={() => setShowCancelConfirm(false)}>
                Keep Subscription
              </ConfirmKeep>
              <ConfirmCancel
                disabled={cancelLoading}
                onClick={async () => {
                  if (!user) return;
                  setCancelLoading(true);
                  const success = await requestCancelSubscription({
                    userId: user.uid,
                    email: user.email || '',
                  });
                  setCancelLoading(false);
                  if (success) {
                    setCancelSuccess(true);
                    setShowCancelConfirm(false);
                  }
                }}
              >
                {cancelLoading ? 'Submitting...' : 'Yes, Cancel'}
              </ConfirmCancel>
            </ConfirmActions>
          </ConfirmModal>
        </ConfirmOverlay>
      )}

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => {
          setIsAuthModalOpen(false);
          setAuthForCancel(false);
        }}
        planName={authForCancel ? undefined : selectedPlanForAuth?.name}
        onSuccess={(uid, email) => {
          if (authForCancel) {
            // User signed in to manage subscription — just close modal
            setAuthForCancel(false);
            return;
          }
          if (selectedPlanForAuth) {
            redirectToStripe(selectedPlanForAuth.id, (isYearly ? 'yearly' : 'monthly') as BillingCycle, uid, email);
          }
        }}
      />
    </PageContainer>
  );
};

export default PricingPage;
